import React, { useRef, useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import FieldBottom from './field-bottom';
import './google-maps-autocomplete.scss';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-autocomplete-script';
// Empty array ref for useLoadScript (we load places via importLibrary). Must be stable to avoid reload.
const LOAD_SCRIPT_LIBRARIES = [];

/**
 * Build address data from the new Place class (PlaceAutocompleteElement / Places API New).
 * Uses formattedAddress, location, and optional addressComponents.
 */
function buildAddressDataFromPlace(place) {
    const formattedAddress = place.formattedAddress || '';
    const location = place.location;
    const lat = location && (typeof location.lat === 'function' ? location.lat() : location.lat);
    const lng = location && (typeof location.lng === 'function' ? location.lng() : location.lng);

    let address = formattedAddress;
    let city = '';
    let state = '';
    let zip = '';
    let country = '';

    if (place.addressComponents && place.addressComponents.length) {
        const getComponent = (type) => {
            const c = place.addressComponents.find((ac) =>
                ac.types && ac.types.includes(type)
            );
            return c ? (c.longText || '') : '';
        };
        const getShort = (type) => {
            const c = place.addressComponents.find((ac) =>
                ac.types && ac.types.includes(type)
            );
            return c ? (c.shortText || '') : '';
        };
        const streetNumber = getComponent('street_number');
        const route = getComponent('route');
        address = `${streetNumber} ${route}`.trim() || formattedAddress;
        city = getComponent('locality') || getComponent('sublocality');
        state = getShort('administrative_area_level_1');
        zip = getComponent('postal_code');
        country = getComponent('country');
    }

    return {
        formatted_address: formattedAddress,
        address: address || formattedAddress,
        city,
        state,
        zip,
        country,
        lat: lat != null ? lat : undefined,
        lng: lng != null ? lng : undefined,
    };
}

const GoogleMapsAutocompleteField = ({
    input,
    label,
    required,
    meta: { touched, error, warning, submitFailed },
    placeholder,
    disabled,
    returnFormat = 'string', // 'string' or 'object'
}) => {
    const containerRef = useRef(null);
    const elementRef = useRef(null);
    const selectListenerRef = useRef(null);
    const inputOnChangeRef = useRef(input?.onChange);
    inputOnChangeRef.current = input?.onChange;

    const [placesLibraryReady, setPlacesLibraryReady] = useState(false);
    const [placesLoadError, setPlacesLoadError] = useState(null);

    const { isLoaded: scriptLoaded, loadError: scriptLoadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        id: GOOGLE_MAPS_SCRIPT_ID,
        libraries: LOAD_SCRIPT_LIBRARIES,
    });

    // Load new Places library (PlaceAutocompleteElement) via importLibrary
    useEffect(() => {
        if (!scriptLoaded || !window.google?.maps?.importLibrary) {
            return;
        }
        let cancelled = false;
        window.google.maps.importLibrary('places')
            .then((placesLibrary) => {
                if (cancelled) return;
                if (placesLibrary && placesLibrary.PlaceAutocompleteElement) {
                    setPlacesLibraryReady(true);
                } else {
                    setPlacesLoadError(new Error('PlaceAutocompleteElement not available. Enable Places API (New) in Google Cloud.'));
                }
            })
            .catch((err) => {
                if (!cancelled) setPlacesLoadError(err);
            });
        return () => { cancelled = true; };
    }, [scriptLoaded]);

    // Create PlaceAutocompleteElement and attach to container
    useEffect(() => {
        const container = containerRef.current;
        if (!placesLibraryReady || !container || !window.google?.maps?.places?.PlaceAutocompleteElement) {
            return;
        }

        const PlaceAutocompleteElement = window.google.maps.places.PlaceAutocompleteElement;
        const options = {
            placeholder: placeholder || 'Start typing an address...',
            includedRegionCodes: ['us'],
        };

        const el = new PlaceAutocompleteElement(options);
        if (disabled) el.disabled = true;
        el.style.width = '100%';
        el.classList.add('form-field__place-autocomplete');
        elementRef.current = el;

        const handleSelect = async (event) => {
            const placePrediction = event.placePrediction;
            if (!placePrediction) return;
            const onChange = inputOnChangeRef.current;
            if (!onChange) return;
            try {
                const place = placePrediction.toPlace();
                await place.fetchFields({
                    fields: ['formattedAddress', 'location', 'addressComponents'],
                });

                if (returnFormat === 'object') {
                    const addressData = buildAddressDataFromPlace(place);
                    onChange(addressData);
                } else {
                    onChange(place.formattedAddress || '');
                }
            } catch (err) {
                console.error('Place Autocomplete fetchFields error:', err);
            }
        };

        el.addEventListener('gmp-select', handleSelect);
        selectListenerRef.current = { el, handleSelect };

        container.appendChild(el);

        return () => {
            if (selectListenerRef.current) {
                selectListenerRef.current.el.removeEventListener('gmp-select', selectListenerRef.current.handleSelect);
                selectListenerRef.current = null;
            }
            if (container && el && el.parentNode === container) {
                container.removeChild(el);
            }
            elementRef.current = null;
        };
    }, [placesLibraryReady, placeholder, returnFormat, disabled]);

    // Show the value the form already holds (restored step, prefill) in the element
    useEffect(() => {
        const el = elementRef.current;
        if (!el) return;
        const value = input?.value;
        const text =
            typeof value === 'string'
                ? value
                : (value && (value.formatted_address || value.address)) || '';
        if (text && el.value !== text) el.value = text;
    }, [placesLibraryReady, input?.value]);

    // Sync disabled state
    useEffect(() => {
        if (elementRef.current) elementRef.current.disabled = !!disabled;
    }, [disabled]);

    const hasError = (touched || submitFailed) && error;
    const className = `form-field${hasError ? ' form-field--error' : ''}`;
    const hintMessage = null;

    const loadError = scriptLoadError || placesLoadError;

    if (loadError) {
        return (
            <div className={className}>
                {label && (
                    <label className={required ? "label label_required" : "label"}>
                        {label}
                    </label>
                )}
                <input
                    type="text"
                    className="form-field__control"
                    placeholder="Error loading Google Maps"
                    disabled
                />
                <FieldBottom meta={{ touched, error: 'Failed to load Google Maps', warning }} />
            </div>
        );
    }

    if (!scriptLoaded || !placesLibraryReady) {
        return (
            <div className={className}>
                {label && (
                    <label className={required ? "label label_required" : "label"}>
                        {label}
                    </label>
                )}
                <input
                    type="text"
                    className="form-field__control"
                    placeholder="Loading..."
                    disabled
                />
                <FieldBottom meta={{ touched, error, warning }} />
            </div>
        );
    }

    return (
        <div className={className}>
        {label && (
            <label className={required ? "label label_required" : "label"}>
                {label}
            </label>
        )}
            <div ref={containerRef} className="form-field__place-autocomplete-wrapper" style={{ width: '100%' }} />
            <FieldBottom meta={{ touched, error: hasError ? error : null, warning }} note={hintMessage} />
        </div>
    );
};

export default GoogleMapsAutocompleteField;
